import { Game, Player, PlayerID } from "@kosmod/sesame"
import { createSceneHost, createSceneSessionClient, SceneSessionAction, SceneSessionEvent, TheatreDB } from "@kosmod/theatre"

import { ConciergeDB, Room } from "../defs"
import { Channel } from "@kosmod/pancake";
import { RoomSessionAction, RoomSessionEvent } from "../defs/api";
import { ID, Logger, SubjectData } from "@kosmod/kit";

import { createVoiceSessionClient, createVoiceSessionHost, VoiceSessionAction, VoiceSessionEvent } from "./voiceSession";

export type RoomHost = {
  connect(player: Player, port: Channel.Port.Server<RoomSessionChannel>): Promise<{
      disconnect(): void;
  }>;
  sync(): Promise<void>;
  tick(): void;
}

export const createRoomHost = async (
  game: Game,
  initialRoom: Room,

  logger: Logger,
  dbs: { concierge: ConciergeDB, theatre: TheatreDB }
): Promise<RoomHost> => {
  type User = { id: ID<"user"> };
  let activeRoom = initialRoom;
  let needsSync = false;

  const users = new Map();

  const initialScene = await dbs.theatre.scenes.read({ game: game.id, id: activeRoom.scene });

  logger.log(`Loaded initial scene:`, initialScene)

  const sceneHost = createSceneHost(game, { ...initialScene!, chunks: [] }, logger.createSubsystem(`SceneHost`), dbs.theatre);

  const voice = createVoiceSessionHost(logger.createSubsystem('voice'));

  const connectionByPlayer = new Map<PlayerID, Channel.Port.Server<RoomSessionChannel>>()

  const broadcast = (message: RoomSessionEvent) => {
    logger.createSubsystem('SEND').log(message);
    for (const port of connectionByPlayer.values())
      port.send(message);
  }

  return {
    async connect(player: Player, port: Channel.Port.Server<RoomSessionChannel>) {
      connectionByPlayer.set(player.id, port);

      activeRoom.players.push(player.id);
      await dbs.concierge.rooms.update(activeRoom);

      needsSync = true;
      
      const sceneSession = await sceneHost.connect(player, Channel.map(port)(roomToSceneChannelMap));
      const voiceSession = voice.connect(player, Channel.map(port)(roomToVoiceChannelMap))

      broadcast({ type: 'players', players: activeRoom.players });

      return {
        disconnect() {
          sceneSession.disconnect();
          voiceSession.disconnect();

          activeRoom.players = activeRoom.players.filter(p => p !== player.id);
          broadcast({ type: 'players', players: activeRoom.players });

          dbs.concierge.rooms.update(activeRoom);
          needsSync = true;
        }
      }
    },
    async sync() {
      if (!needsSync)
        return;
      needsSync = false;
      
      await dbs.concierge.rooms.update(activeRoom);
      await sceneHost.sync();
    },
    tick() {
      sceneHost.tick();
    }
  }
}

export type RoomSessionChannel = Channel.Type<RoomSessionAction, RoomSessionEvent>;

// convert room messages to scene messages
const roomToSceneChannelMap = {
  send: (event: SceneSessionEvent): RoomSessionEvent => ({ type: 'scene', scene: event }),
  receive: (action: RoomSessionAction): null | SceneSessionAction => action.type === 'scene' && action.scene || null,
}

const roomToVoiceChannelMap = {
  send: (voice: VoiceSessionEvent): RoomSessionEvent => ({ type: 'voice', voice }),
  receive: (action: RoomSessionAction): null | VoiceSessionAction => action.type === 'voice' && action.voice || null,
}

export type RoomSessionClient = ReturnType<typeof createRoomSessionClient>;
export const createRoomSessionClient = (self: Player, port: Channel.Port.Client<RoomSessionChannel>, logger: Logger) => {
  const players = new SubjectData(new Set<PlayerID>());

  const voice = createVoiceSessionClient(self, Channel.map(port)({
    send(send) {
      return { type: 'voice', voice: send }
    },
    receive(receive) {
      if (receive.type !== 'voice')
        return null;
      return receive.voice;
    },
  }));
  const scene = createSceneSessionClient(Channel.map(port)({
    send(message) {
      return { type: 'scene', scene: message };
    },
    receive(message) {
      if (message.type !== 'scene')
        return null;
      return message.scene;
    }
  }), logger.createSubsystem('SceneHost'), self)

  const sub = port.receive.subscribe((event) => {
    switch (event.type) {
      case 'players':
        players.current.clear();
        for (const player of event.players)
          players.current.add(player);
        players.notify();
        return;
    }
  });

  const disconnect = () => {
    sub.unsubscribe()
  }

  return {
    subjects: {
      players,
    },
    voice,
    scene,

    disconnect,
  }
}